import { Property, PropertyFilters, PropertySearchParams } from '@/types/property';
import { BlogPost } from '@/types/blog';
import { ContactFormData, ApiResponse } from '@/types/api';
import api from '@/services/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

export async function getProperties(params?: Partial<PropertySearchParams>, companyId?: string): Promise<ApiResponse<Property[]>> {
  try {
    const queryParams = new URLSearchParams();

    // Add companyId if provided
    if (companyId) queryParams.append('companyId', companyId);

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    if (params?.filters) {
      const { filters } = params;
      if (filters.operation) queryParams.append('operation', filters.operation);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.minPrice !== undefined) queryParams.append('minPrice', filters.minPrice.toString());
      if (filters.maxPrice !== undefined) queryParams.append('maxPrice', filters.maxPrice.toString());
      if (filters.bedrooms !== undefined) queryParams.append('bedrooms', filters.bedrooms.toString());
      if (filters.location) queryParams.append('location', filters.location);
      if (filters.search) queryParams.append('search', filters.search);
    }

    const url = `${API_URL}/properties${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error fetching properties');
    }

    return await response.json();
  } catch (error) {
    // Error will be handled by calling component with toast
    return {
      data: [],
      success: false,
      message: 'Error al cargar propiedades'
    };
  }
}

export async function getProperty(id: string): Promise<ApiResponse<Property | null>> {
  try {
    const url = `${API_URL}/properties/${id}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Property not found');
    }

    return await response.json();
  } catch (error) {
    // Error will be handled by calling component with toast
    return {
      data: null,
      success: false,
      message: 'Propiedad no encontrada'
    };
  }
}

export async function getFeaturedProperties(companyId?: string): Promise<ApiResponse<Property[]>> {
  try {
    const queryParams = new URLSearchParams();

    // Add companyId if provided
    if (companyId) queryParams.append('companyId', companyId);

    const url = `${API_URL}/properties/featured${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error fetching featured properties');
    }

    return await response.json();
  } catch (error) {
    // Error will be handled by calling component with toast
    return {
      data: [],
      success: false,
      message: 'Error al cargar propiedades destacadas'
    };
  }
}

export async function getBlogPosts(companyId: string, featured?: boolean): Promise<ApiResponse<BlogPost[]>> {
  try {
    let url = `${API_URL}/blog?companyId=${companyId}`;

    if (featured !== undefined) {
      // Use featured endpoint with companyId filter
      url = `${API_URL}/blog/featured?companyId=${companyId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Fallback: return empty array instead of failing
      return {
        data: [],
        success: true,
        message: 'No hay artículos disponibles'
      };
    }

    const result = await response.json();
    return {
      data: result.data || [],
      success: result.success,
      message: result.message
    };
  } catch (error) {
    // Graceful degradation: return empty array
    // Error will be handled by calling component with toast if needed
    return {
      data: [],
      success: true,
      message: 'Blog no disponible'
    };
  }
}

export async function getBlogPost(slug: string, companyId: string): Promise<ApiResponse<BlogPost | null>> {
  try {
    const url = `${API_URL}/blog/slug/${slug}?companyId=${companyId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {
          data: null,
          success: false,
          message: 'Post no encontrado'
        };
      }
      throw new Error('Error fetching blog post');
    }

    const result = await response.json();
    return {
      data: result.data,
      success: result.success,
      message: result.message
    };
  } catch (error) {
    // Error will be handled by calling component with toast
    return {
      data: null,
      success: false,
      message: 'Error al cargar el post'
    };
  }
}

export async function submitContact(data: ContactFormData, companyId: string): Promise<ApiResponse<boolean>> {
  try {
    const url = `${API_URL}/leads/public`;

    // Map frontend fields to backend fields
    const requestData = {
      name: data.name || data.nombre,
      email: data.email || data.correo,
      phone: data.phone || data.telefono,
      message: data.message || data.mensaje,
      companyId: companyId
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Error al enviar mensaje');
    }

    return {
      data: true,
      success: true,
      message: result.message || 'Mensaje enviado correctamente'
    };
  } catch (error: any) {
    // Error will be handled by calling component with toast
    return {
      data: false,
      success: false,
      message: error.message || 'Error al enviar mensaje. Por favor intenta nuevamente.'
    };
  }
}

export async function getSimilarProperties(propertyId: string, limit = 3, companyId?: string): Promise<ApiResponse<Property[]>> {
  try {
    // Get the property first to determine type and operation
    const propertyResponse = await getProperty(propertyId);
    if (!propertyResponse.success || !propertyResponse.data) {
      return { data: [], success: false, message: 'Property not found' };
    }

    const currentProperty = propertyResponse.data;

    // Fetch all properties with same type or operation
    const allPropertiesResponse = await getProperties({
      filters: {
        type: currentProperty.type,
        operation: currentProperty.operation,
      },
      limit: limit + 1, // Get one extra to exclude current property
    }, companyId);

    if (!allPropertiesResponse.success) {
      return { data: [], success: false };
    }

    // Filter out current property and limit results
    const similar = allPropertiesResponse.data
      .filter(p => p.id !== propertyId)
      .slice(0, limit);

    return {
      data: similar,
      success: true
    };
  } catch (error) {
    // Error will be handled by calling component with toast
    return {
      data: [],
      success: false,
      message: 'Error al cargar propiedades similares'
    };
  }
}